import React, { useEffect, useRef, useState } from "react";
import { css, SerializedStyles } from "@emotion/react";

import DropdownButtonContainer from "./components/DropdownButtonContainer";
import DropdownButtonPlaceholder from "./components/DropdownButtonPlaceholder";
import DropdownButtonPlaceholderContainer from "./components/DropdownButtonPlaceholderContainer";
import DropdownButtonOptionsContainer from "./components/DropdownButtonOptionsContainer";
import DropdownButtonOptionContainer from "./components/DropdownButtonOptionContainer";
import DropdownButtonOptionLabel from "./components/DropdownButtonOptionLabel";
import DropdownButtonArrow from "./components/DropdownButtonArrow";
import DropdownButtonScroll from "./components/DropdownButtonScroll";
import theme from "../../../../theme";

type Props = {
    className?: string,
    options: Option[],
    onChange?: (item: Option) => void,
    onChangeMultiple?: (items: Option[]) => void,
    selectedValue: Option[],
    customOptionStyle?: SerializedStyles,
    customOptionContainerStyle?: SerializedStyles,
    isLargeLabel?: boolean,
    defaultName?: string,
    backgroundColour?: string,
    optionBackgroundColour?: string,
    optionBackgroundHoveredColour?: string,
    useInnerScroll?: boolean,
    zIndex?: number,
};

const DropdownButton: React.FC<Props> = ({
    className,
    options,
    onChange,
    onChangeMultiple,
    selectedValue,
    customOptionStyle,
    customOptionContainerStyle,
    isLargeLabel = false,
    defaultName,
    backgroundColour,
    optionBackgroundColour = theme.colours.pictonBlue,
    optionBackgroundHoveredColour = theme.colours.malibu,
    useInnerScroll = true,
    zIndex = 100,
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [multipleValue, setMultipleValue] = useState<MultipleOptions>();
    const inputRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: any) => {
            if (inputRef.current && !inputRef.current.contains(e.target)) {
                setShowMenu(false);
            }
        };

        window.addEventListener("click", handler);

        return () => {
            window.removeEventListener("click", handler);
        };
    }, []);

    useEffect(() => {
        if (multipleValue) {
            let selected = false;
            for (const [, value] of Object.entries(multipleValue)) {
                if (value) selected = true;
            }

            // In case nothing selected, select all
            if (!selected) {
                const optionValues: MultipleOptions = {};
                options.map((option) => {
                    return optionValues[option.value] = true;
                });
                setMultipleValue(optionValues);
            }
        }
    }, [multipleValue, options]);

    const handleInputClick = () => {
        setShowMenu(!showMenu);
    };

    return (
        <div css={containerStyle} className={className}>
            <DropdownButtonScroll dropdownRef={dropdownRef} options={options} showMenu={showMenu} />
            <DropdownButtonContainer dropdownRef={dropdownRef}>
                <DropdownButtonPlaceholderContainer
                    optionBackgroundColour={optionBackgroundColour}
                    backgroundColour={backgroundColour}
                    handleInputClick={handleInputClick}
                    inputRef={inputRef}
                    showMenu={showMenu}
                    zIndex={zIndex}
                    className={"placeholder-container"}
                >
                    <DropdownButtonPlaceholder
                        selectedValue={selectedValue}
                        options={options}
                        isLargeLabel={isLargeLabel}
                        defaultName={defaultName}
                    />
                    <DropdownButtonArrow showMenu={showMenu} optionBackgroundColour={optionBackgroundColour} />
                </DropdownButtonPlaceholderContainer>

                <DropdownButtonOptionsContainer
                    customOptionContainerStyle={customOptionContainerStyle}
                    optionBackgroundColour={optionBackgroundColour}
                    zIndex={zIndex - 1}
                    useInnerScroll={useInnerScroll}
                    optionCount={options.length}
                    showMenu={showMenu}
                >
                    {options.map((option: Option, index: number) => (
                        <DropdownButtonOptionContainer
                            key={`${option.value}-${index}`}
                            options={options}
                            option={option}
                            multipleValue={multipleValue}
                            setMultipleValue={setMultipleValue}
                            onChangeMultiple={onChangeMultiple}
                            onChange={onChange}
                            customOptionStyle={customOptionStyle}
                            optionBackgroundHoveredColour={optionBackgroundHoveredColour}
                        >
                            <DropdownButtonOptionLabel option={option}/>
                        </DropdownButtonOptionContainer>
                    ))}
                </DropdownButtonOptionsContainer>
            </DropdownButtonContainer>
        </div>
    );
}

const containerStyle = css`
    position: relative;
    border-radius: 10px 10px 0 0;
    width: 100%;
`;

export default DropdownButton;
